import { eachWeekOfInterval, endOfWeek, getWeek, startOfWeek } from "date-fns";

import { WeekStartsOn } from "@pdpp/lib-planta";

//Formats minutes to hours and minutes, example --> 5h34
export function fmtHhMm(mins: number): string {
  return `${Math.floor(mins / 60).toLocaleString()}h${(mins % 60)
    .toString()
    .padStart(2, "0")}`;
}

//Format decimals hours, example "3.5" to "3h30"
export function formatDecimalHours(decimalHours: number): string {
  const totalMinutes = Math.round(decimalHours * 60);
  const hours = Math.floor(totalMinutes / 60).toLocaleString();
  const minutes = totalMinutes % 60;

  return `${hours}h${minutes.toString().padStart(2, "0")}`;
}

export function getDatesInMonth(
  year: number,
  month: number,
  weekStartsOn: WeekStartsOn,
): ReadonlyArray<Date> {
  const firstDayOfMonth = new Date(year, month, 1);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const result: Array<Date> = [];
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  for (
    let i = daysInPrevMonth - ((firstDayOfWeek - weekStartsOn + 7) % 7) + 1;
    i <= daysInPrevMonth;
    i++
  ) {
    result.push(new Date(year, month - 1, i));
  }

  for (let day = 1; day <= new Date(year, month + 1, 0).getDate(); day++) {
    result.push(new Date(year, month, day));
  }

  const daysToAddNextMonth = 7 - (result.length % 7);

  if (daysToAddNextMonth < 7) {
    for (let i = 1; i <= daysToAddNextMonth; i++) {
      result.push(new Date(year, month + 1, i));
    }
  }

  return result;
}

export function getMonthsAndYearsRange(
  startDate: Date,
  endDate: Date,
): ReadonlyArray<{
  readonly year: number;
  readonly month: number;
}> {
  const monthsAndYears: Array<{
    readonly year: number;
    readonly month: number;
  }> = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    monthsAndYears.push({ year, month });

    // Move to the next month
    currentDate.setMonth(month + 1);
  }

  return monthsAndYears;
}

export const getMinutes = (values: ReadonlyArray<number>): number => {
  let total = 0;

  for (const value of values) {
    total = total + value;
  }

  return total * 15;
};

// Returns last day of the month, e.g. January => 31, February => 28 or 29
export function getLastDayOfMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

// Returns week numbers for the specified period.
export function getWeeksInRange(
  start: Date,
  end: Date,
  weekStartsOn: WeekStartsOn,
): ReadonlyArray<number> {
  // Adjust the start and end dates to the beginning and end of their respective weeks
  const adjustedStart = startOfWeek(start, { weekStartsOn });
  const adjustedEnd = endOfWeek(end, { weekStartsOn });

  // Get all the weeks in the interval
  const weeksInRange = eachWeekOfInterval(
    { start: adjustedStart, end: adjustedEnd },
    { weekStartsOn },
  );

  const weekNumbers = weeksInRange.map((date) =>
    getWeek(date, { weekStartsOn, firstWeekContainsDate: 4 }),
  );

  return weekNumbers;
}
